// Single global voice-note player (WhatsApp-style): playback survives leaving
// the conversation; Shell shows a mini bar while it plays elsewhere.
import { api, mediaSrc } from "../api";
import { playbackFocusAcquire, playbackFocusRelease } from "../lib/wakeword";

export const getVoiceSpeed = () => {
  const v = parseFloat(localStorage.getItem("osc_voice_speed") || "1");
  return v === 1.5 || v === 2 ? v : 1;
};

export const fmtSecs = (s) => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

let state = { id: null, convId: null, sender: "", media: null, createdAt: null, inView: false, playing: false, loading: false, cur: 0, dur: 0 };
const listeners = new Set();
const blobUrls = new Map();
let audio = null;
let focus = false;
let onEndedCb = null;

const emit = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn(state));
};

export const getVoiceState = () => state;
if (typeof window !== "undefined") window.__voiceDebug = { getVoiceState: () => state, playNextFromServer: () => playNextFromServer() }; // debug hook
export function subscribeVoice(fn) {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

const takeFocus = async () => { if (!focus) { focus = true; await playbackFocusAcquire(); } };
const dropFocus = () => { if (focus) { focus = false; playbackFocusRelease(); } };

function ensureAudio() {
  if (audio) return audio;
  audio = document.createElement("audio");
  audio.preload = "none";
  audio.setAttribute("data-voice-audio", "1");
  audio.addEventListener("play", () => { try { audio.playbackRate = getVoiceSpeed(); } catch (e) {} emit({ playing: true }); });
  audio.addEventListener("pause", () => { emit({ playing: false }); dropFocus(); });
  audio.addEventListener("timeupdate", () => emit({ cur: audio.currentTime || 0 }));
  const setDur = () => { if (isFinite(audio.duration) && audio.duration > 0) emit({ dur: audio.duration }); };
  audio.addEventListener("loadedmetadata", setDur);
  audio.addEventListener("durationchange", setDur);
  audio.addEventListener("error", () => { emit({ playing: false, loading: false }); dropFocus(); });
  audio.addEventListener("ended", async () => {
    emit({ playing: false, cur: 0 });
    dropFocus();
    const cb = onEndedCb;
    onEndedCb = null;
    const endedId = state.id;
    // WhatsApp-style chain: next note on screen, else the next one in the conversation (server)
    const handled = cb ? cb() : false;
    if (!handled) await playNextFromServer();
    if (state.id === endedId && !state.loading && !state.playing) stopVoiceNote();
  });
  return audio;
}

async function ensureBlob(media) {
  if (blobUrls.has(media.id)) return blobUrls.get(media.id);
  const r = await fetch(mediaSrc(media));
  if (!r.ok) throw new Error(`http ${r.status}`);
  let b = await r.blob();
  if (!b.type || b.type === "application/octet-stream") b = new Blob([b], { type: "audio/mp4" });
  const url = URL.createObjectURL(b);
  blobUrls.set(media.id, url);
  return url;
}

const senderNames = new Map();
const playedSent = new Set(); // message ids already reported as played (WhatsApp "Played by")
function reportPlayed(id) {
  if (!id || playedSent.has(id)) return;
  playedSent.add(id);
  try {
    const me = JSON.parse(localStorage.getItem("pmf_user") || "null");
    if (me?.id) api.post(`/messages/${id}/played`, { user_id: me.id }).catch(() => {});
  } catch (e) { /* ignore */ }
}
async function senderName(uid) {
  if (!uid) return "";
  if (!senderNames.has(uid)) {
    try { senderNames.set(uid, (await api.get(`/users/${uid}`)).data?.name || ""); } catch (e) { return ""; }
  }
  return senderNames.get(uid);
}

/** Chain after leaving the chat: next voice note of the same conversation, from the server. */
async function playNextFromServer() {
  const { convId, createdAt, id } = state;
  if (!convId || !createdAt) return;
  try {
    const me = JSON.parse(localStorage.getItem("pmf_user") || "null");
    const { data } = await api.get(`/conversations/${convId}/messages`, { params: { user_id: me?.id, after: createdAt, limit: 40 } });
    const list = Array.isArray(data) ? data : [];
    const i = list.findIndex((m) => m.id === id);
    const next = list.slice(i + 1).find((m) => m.type === "voice" && m.media?.[0]);
    if (!next || state.id !== id) return;
    await playVoiceNote({
      id: next.id, convId, sender: await senderName(next.sender_id), media: next.media[0],
      durationHint: next.meta?.duration, createdAt: next.created_at,
    });
  } catch (e) { /* end of chain */ }
}

/** The active bubble reports whether it is on screen (mini bar shows when it is not). */
export function setVoiceInView(id, inView) {
  if (state.id === id && state.inView !== inView) emit({ inView });
}

/** Start (or resume) a voice note. `onEnded` (returns true when it chained) is used for WhatsApp-style chaining. */
export async function playVoiceNote({ id, convId, sender, media, durationHint, createdAt, inView = false, onEnded }) {
  if (state.loading) return;
  const a = ensureAudio();
  if (state.id === id && a.src) {
    onEndedCb = onEnded || null;
    await takeFocus();
    try { await a.play(); } catch (e) { dropFocus(); }
    return;
  }
  try { a.pause(); } catch (e) {}
  emit({ id, convId, sender: sender || "", media, createdAt: createdAt || null, inView, loading: true, cur: 0, dur: Number(durationHint) || 0, playing: false });
  onEndedCb = onEnded || null;
  try {
    const [url] = await Promise.all([ensureBlob(media), takeFocus()]);
    if (state.id !== id) return; // another note was requested meanwhile
    a.src = url;
    try { a.playbackRate = getVoiceSpeed(); } catch (e) {}
    await a.play();
    reportPlayed(id);
  } catch (e) {
    dropFocus();
    if (state.id === id) emit({ id: null, convId: null, media: null, sender: "", createdAt: null, inView: false, playing: false, loading: false, cur: 0, dur: 0 });
    throw e;
  } finally {
    if (state.id === id) emit({ loading: false });
  }
}

export function pauseVoiceNote() { try { audio?.pause(); } catch (e) {} }

export function toggleVoiceNote() {
  if (!audio || !state.id) return;
  if (state.playing) pauseVoiceNote();
  else { takeFocus().then(() => audio.play().catch(() => dropFocus())); }
}

export function seekVoiceNote(pct) {
  if (!audio || !isFinite(audio.duration)) return;
  audio.currentTime = Math.min(1, Math.max(0, pct)) * audio.duration;
  emit({ cur: audio.currentTime });
}

export function setVoiceRate(rate) {
  if (!audio) return;
  try { audio.playbackRate = rate; audio.defaultPlaybackRate = rate; } catch (e) {}
}

export function stopVoiceNote() {
  pauseVoiceNote();
  if (audio) { try { audio.removeAttribute("src"); audio.load(); } catch (e) {} }
  onEndedCb = null;
  emit({ id: null, convId: null, media: null, sender: "", createdAt: null, inView: false, playing: false, loading: false, cur: 0, dur: 0 });
}
